import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getSubsidiaryProducts,
  updateSubsidiaryProduct,
} from '../services/product-catalog.service';
import type { UpdateSubsidiaryProductPayload } from '../services/product-catalog.service';

/**
 * Fetches enrolled products for a subsidiary (GET /subsidiaries/:id/products).
 * Skipped when subsidiaryId is falsy (e.g. pre-auth).
 */
export function useSubsidiaryProducts(subsidiaryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.products.bySubsidiary(subsidiaryId ?? ''),
    queryFn: () => getSubsidiaryProducts(subsidiaryId ?? ''),
    enabled: !!subsidiaryId,
  });
}

/**
 * Patches contract details for a single enrolled product.
 * PATCH /subsidiaries/:subsidiaryId/products/:productCode
 * Invalidates the subsidiary's product list on success.
 */
export function useUpdateSubsidiaryProduct(subsidiaryId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      productCode,
      payload,
    }: {
      productCode: string;
      payload: UpdateSubsidiaryProductPayload;
    }) => updateSubsidiaryProduct(subsidiaryId, productCode, payload),
    onSuccess: () => {
      toast.success('Product updated successfully.');
      void qc.invalidateQueries({ queryKey: queryKeys.products.bySubsidiary(subsidiaryId) });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to update product'));
    },
  });
}
